
import Vue, { PropType } from 'vue'
import api from '@root/src/api/api'
import SpinnerLoader from '@root/src/components/SpinnerLoader.vue'
import Opz2Metrics from './Opz2Metrics.vue'
import Opz2Empty from './Opz2Empty.vue'
import { numSort, floatNumSort } from '@root/src/utils'
import {
    EMPTY_DICTIONARIES,
    Opz2Dictionaries,
    kpiAccountLabel,
    formatNumber,
    formatPercent,
    totalCancels,
    totalOpz,
} from './opz2'

export default Vue.extend({
    name: 'Opz2KpiTab',
    components: { SpinnerLoader, Opz2Metrics, Opz2Empty },
    props: {
        projectId: { type: String, required: true },
        filters: { type: Object as PropType<any>, required: true },
        active: { type: Boolean, default: false },
    },
    data: () => ({
        loading: false,
        error: false,
        requestId: 0,
        unclassifiedEvents: 0,
        reportData: null as any,
        dictionaries: EMPTY_DICTIONARIES as Opz2Dictionaries,
        accrualConfigured: false,
        footerProps: { 'items-per-page-options': [10, 25, 50] },
        rules: [
            {
                title: 'Как читать итог',
                text: 'Всего ОПЗ за период — это сумма ОПЗ 2.0 и кросс-продаж.',
            },
            {
                title: 'Визиты как база сравнения',
                text:
                    'Проценты показывают долю ОПЗ и кросс-продаж от всех визитов за выбранный период записи.',
            },
            {
                title: 'Отмены разделены',
                text:
                    'Отменено ОПЗ и отменено кросс считаются отдельно, чтобы не смешивать разные действия админа.',
            },
        ],
        headers: [
            { text: 'Аккаунт', value: 'account', align: 'left', width: '20%' },
            { text: 'Всего визитов', value: 'visits', sort: numSort, align: 'center' },
            { text: 'ОПЗ 2.0', value: 'opz', sort: numSort, align: 'center' },
            { text: '% ОПЗ от визитов', value: 'opzPercent', sort: floatNumSort, align: 'center' },
            { text: 'Кросс-продажи', value: 'cross', sort: numSort, align: 'center' },
            {
                text: '% кросс от визитов',
                value: 'crossPercent',
                sort: floatNumSort,
                align: 'center',
            },
            { text: 'Впервые', value: 'firstly', sort: numSort, align: 'center' },
            { text: 'Начислено', value: 'accrued', sort: numSort, align: 'center' },
        ],
        cancelHeaders: [
            { text: 'Аккаунт', value: 'account', align: 'left', width: '22%' },
            { text: 'ОПЗ 2.0', value: 'opz', sort: numSort, align: 'center' },
            { text: 'Отменено ОПЗ', value: 'opzCancel', sort: numSort, align: 'center' },
            {
                text: '% отмен ОПЗ',
                value: 'opzCancelPercent',
                sort: floatNumSort,
                align: 'center',
            },
            { text: 'Кросс-продажи', value: 'cross', sort: numSort, align: 'center' },
            { text: 'Отменено кросс', value: 'crossCancel', sort: numSort, align: 'center' },
            {
                text: '% отмен кросс',
                value: 'crossCancelPercent',
                sort: floatNumSort,
                align: 'center',
            },
        ],
    }),
    computed: {
        list(): any[] {
            return this.reportData?.kpiOpz ?? []
        },
        total(): any {
            return this.reportData?.kpiOpzTotal ?? {}
        },
        hasData(): boolean {
            return this.list.length > 0
        },
        accruedTotal(): number {
            return Number(
                this.total.accrued ??
                    this.list.reduce(
                        (sum: number, row: any) => sum + Number(row.accrued ?? 0),
                        0
                    )
            )
        },
        /** Лидер по доле кросс-продаж от визитов — подсветка tr.hot из макета. */
        topCrossPercent(): number {
            return this.list.reduce(
                (max: number, row: any) => Math.max(max, Number(row.recordedOtherSpecAdminPercent ?? 0)),
                0
            )
        },
        metrics(): any[] {
            const visits = Number(this.total.visitsAdminCnt ?? 0)
            const opz = Number(this.total.recordedSameSpecAdminCnt ?? 0)
            const cross = Number(this.total.recordedOtherSpecAdminCnt ?? 0)
            return [
                {
                    label: 'Всего визитов',
                    value: formatNumber(visits),
                    hint: 'за период записи',
                },
                {
                    label: 'ОПЗ 2.0',
                    value: formatNumber(opz),
                    hint: `${formatPercent(this.total.recordedSameSpecAdminPercent)} от всех визитов`,
                    tone: 'wasabi',
                },
                {
                    label: 'Кросс-продажи',
                    value: formatNumber(cross),
                    hint: `${formatPercent(this.total.recordedOtherSpecAdminPercent)} от всех визитов`,
                    tone: 'teal',
                },
                {
                    label: 'Впервые',
                    value: formatNumber(this.total.recordedOtherSpecFirstlyAdminCnt),
                    hint: 'входит в кросс-продажи',
                    tone: 'amber',
                },
                {
                    label: 'Начислено',
                    value: `${formatNumber(this.accruedTotal)} ₽`,
                    hint: 'по активной схеме KPI',
                },
            ]
        },
        rows(): any[] {
            return this.list.map((row: any) => {
                const crossPercent = Number(row.recordedOtherSpecAdminPercent ?? 0)
                return {
                    account: kpiAccountLabel(this.dictionaries, row.opzCreatingUserId),
                    visits: formatNumber(row.visitsAdminCnt),
                    opz: formatNumber(row.recordedSameSpecAdminCnt),
                    opzPercent: formatPercent(row.recordedSameSpecAdminPercent),
                    opzTone: 'base',
                    cross: formatNumber(row.recordedOtherSpecAdminCnt),
                    crossPercent: formatPercent(crossPercent),
                    crossTone:
                        this.topCrossPercent > 0 && crossPercent >= this.topCrossPercent
                            ? 'hi'
                            : 'base',
                    firstly: formatNumber(row.recordedOtherSpecFirstlyAdminCnt),
                    accrued: `${formatNumber(row.accrued)} ₽`,
                    isHot:
                        this.topCrossPercent > 0 && crossPercent >= this.topCrossPercent,
                }
            })
        },
        totalRow(): any {
            if (!this.list.length) return null
            return {
                account: 'Итого',
                visits: formatNumber(this.total.visitsAdminCnt),
                opz: formatNumber(this.total.recordedSameSpecAdminCnt),
                opzPercent: formatPercent(this.total.recordedSameSpecAdminPercent),
                cross: formatNumber(this.total.recordedOtherSpecAdminCnt),
                crossPercent: formatPercent(this.total.recordedOtherSpecAdminPercent),
                firstly: formatNumber(this.total.recordedOtherSpecFirstlyAdminCnt),
                accrued: `${formatNumber(this.accruedTotal)} ₽`,
            }
        },
        cancelRows(): any[] {
            return this.list.map((row: any) => ({
                account: kpiAccountLabel(this.dictionaries, row.opzCreatingUserId),
                opz: formatNumber(row.recordedSameSpecAdminCnt),
                opzCancel: formatNumber(row.recordedSameSpecCancelAdminCnt),
                opzCancelCnt: Number(row.recordedSameSpecCancelAdminCnt ?? 0),
                opzCancelPercent: formatPercent(row.recordedSameSpecCancelAdminPercent),
                cross: formatNumber(row.recordedOtherSpecAdminCnt),
                crossCancel: formatNumber(row.recordedOtherSpecCancelAdminCnt),
                crossCancelCnt: Number(row.recordedOtherSpecCancelAdminCnt ?? 0),
                crossCancelPercent: formatPercent(row.recordedOtherSpecCancelAdminPercent),
                isHot:
                    this.topCrossPercent > 0 &&
                    Number(row.recordedOtherSpecAdminPercent ?? 0) >= this.topCrossPercent,
            }))
        },
        cancelTotalRow(): any {
            if (!this.list.length) return null
            return {
                account: 'Итого',
                opz: formatNumber(this.total.recordedSameSpecAdminCnt),
                opzCancel: formatNumber(this.total.recordedSameSpecCancelAdminCnt),
                opzCancelPercent: formatPercent(this.total.recordedSameSpecCancelAdminPercent),
                cross: formatNumber(this.total.recordedOtherSpecAdminCnt),
                crossCancel: formatNumber(this.total.recordedOtherSpecCancelAdminCnt),
                crossCancelPercent: formatPercent(this.total.recordedOtherSpecCancelAdminPercent),
            }
        },
        cancelTotalLabel(): string {
            const cancels = totalCancels(
                this.total.recordedSameSpecCancelAdminCnt,
                this.total.recordedOtherSpecCancelAdminCnt
            )
            return `${formatNumber(cancels)} ${declineCancels(cancels)} за период`
        },
    },
    watch: {
        active: {
            immediate: true,
            handler() {
                this.loadIfNeeded()
            },
        },
        filters() {
            this.loadIfNeeded()
        },
    },
    beforeDestroy() {
        this.requestId++
    },
    methods: {
        rowClass(row: any): string {
            return row.isHot ? 'opz2-row--hot' : ''
        },
        loadIfNeeded() {
            if (!this.active) return
            this.load()
        },
        async load() {
            const requestId = ++this.requestId
            this.loading = true
            this.error = false
            this.$emit('loading', true)
            try {
                const { data, error } = await api.gcb2.getOpz2Data({
                    projectId: this.projectId,
                    filters: this.filters,
                    tabs: ['kpi'],
                })
                if (requestId !== this.requestId) return
                if (error || !data) {
                    this.error = true
                    return
                }
                this.reportData = data.reportData
                this.unclassifiedEvents = Number(data.meta?.unclassifiedEvents ?? 0)
                this.dictionaries = data.dictionaries ?? EMPTY_DICTIONARIES
                this.accrualConfigured = data.meta?.accrualConfigured === true
            } catch (err) {
                if (requestId === this.requestId) this.error = true
            } finally {
                if (requestId === this.requestId) {
                    this.loading = false
                    this.$emit('loading', false)
                }
            }
        },
    },
})

function declineCancels(count: number): string {
    const tail = count % 100
    if (tail >= 11 && tail <= 14) return 'отмен'
    switch (count % 10) {
        case 1:
            return 'отмена'
        case 2:
        case 3:
        case 4:
            return 'отмены'
        default:
            return 'отмен'
    }
}
